import { computed } from 'vue'
import {
  ACTIVE_STATUS,
  ACTIVE_STATUS_COLORS
} from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'

/**
 * Single source of truth for the active/inactive status shared by the winter
 * service v2 screens - how it is labelled, coloured, offered in a dropdown and
 * rendered in a table cell. Kept in one place so a table cell and the form that
 * edits it can never drift apart.
 */
export function useActiveStatus (options) {
  const { trans } = options || {}

  const getStatusLabel = (status) => status === ACTIVE_STATUS.inactive
    ? trans('base.inactive')
    : trans('base.active')

  const getStatusColors = (status) =>
    ACTIVE_STATUS_COLORS[status] || ACTIVE_STATUS_COLORS[ACTIVE_STATUS.active]

  const statusItems = computed(() => Object.values(ACTIVE_STATUS).map(status => ({
    id: status,
    name: getStatusLabel(status)
  })))

  /**
   * The same two choices for a listing filter. The listing endpoints read the
   * status straight off `is_active`, so these carry 1/0 rather than the
   * `ACTIVE_STATUS` keys the forms edit.
   */
  const activeFilterItems = computed(() => [
    {
      id: 1,
      name: getStatusLabel(ACTIVE_STATUS.active)
    },
    {
      id: 0,
      name: getStatusLabel(ACTIVE_STATUS.inactive)
    }
  ])

  /**
   * Builds the object a `type: 'status'` table cell expects - it reads its label
   * and both dot colours off this single value (see TableCellContent.vue).
   */
  const buildStatusCell = (status) => {
    if (!status) {
      return null
    }

    return {
      description: getStatusLabel(status),
      color: getStatusColors(status)
    }
  }

  // The cellsConfig entry that goes with `buildStatusCell` - spread it into a
  // table's cellsConfig under the status column's key.
  const statusCellConfig = {
    type: 'status',
    displayValue: 'description',
    colorObjectKey: 'color',
    textColorKey: 'text',
    backgroundColorKey: 'background'
  }

  return {
    statusItems,
    activeFilterItems,
    statusCellConfig,
    getStatusLabel,
    getStatusColors,
    buildStatusCell
  }
}
